import {Injectable, Injector} from '@angular/core';
import {HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {BaseService} from './base.service';
import {suppressErrorLog} from '@twpub/shared/interceptors/http-context-tokens';
import {TermwebConfiguration} from '@twpub/core/models';
import {
  ExportApproval,
  ExportApprovalRequestResponse,
  RemoteTenantInfo,
  TenantConfiguration
} from '@twpub/core/models/tenant-configuration';
import {MigrationResponse, TenantImpersonationResponse} from '@twpub/core/models/tenant-responses';
import {SynchronizeDictionariesTaskStatus} from '@twpub/core/models/synchronize-dictionaries-task-status';

@Injectable({
  providedIn: 'root'
})
export class TenantService extends BaseService {
  constructor(injector: Injector) {
    super(injector);
  }

  /**
   * Get current tenant based on request hostname.
   */
  getCurrentTenant(): Observable<TenantConfiguration> {
    return this.http.get<TenantConfiguration>(this.url('/tenants/current'));
  }

  createTenant(tenant: TenantConfiguration): Observable<TenantConfiguration> {
    return this.http.post<TenantConfiguration>(this.url('/tenants'), tenant);
  }

  updateConfiguration(config: TenantConfiguration, dryRun: boolean = false): Observable<any> {
    return this.http.put(this.url('/tenants' + (dryRun ? '?dryRun=true' : '')), config);
  }

  updateTenantById(id: number, config: TenantConfiguration): Observable<TenantConfiguration> {
    return this.http.put<TenantConfiguration>(this.url('/tenants/' + id), config);
  }

  deleteConfiguration(id: number) {
    return this.http.delete(this.url('/tenants/' + id));
  }

  getConfigurations(): Observable<TenantConfiguration[]> {
    return this.http.get<TenantConfiguration[]>(this.url('/tenants'));
  }

  getConfiguration(id: number): Observable<TenantConfiguration> {
    return this.http.get<TenantConfiguration>(this.url('/tenants/' + id));
  }

  impersonateTenant(id: number): Observable<TenantImpersonationResponse> {
    return this.http.post<TenantImpersonationResponse>(this.url('/tenants/' + id + '/impersonate'), {});
  }

  migrateUsers(tenantId: number, sourceRealm: string): Observable<MigrationResponse> {
    return this.http.post<MigrationResponse>(
      this.url(`/tenants/${tenantId}/migrate-users?sourceRealm=${sourceRealm}`), {}
    );
  }

  getScheduledSyncStatus(): Observable<SynchronizeDictionariesTaskStatus> {
    return this.http.get<SynchronizeDictionariesTaskStatus>(this.url('/tenants/current/sync-status'));
  }

  runSyncNow(): Observable<void> {
    return this.http.post<void>(this.url('/tenants/current/run-sync'), {});
  }

  updateDictionaryOrder(dictionaryOrder: number[]): Observable<void> {
    return this.http.put<void>(this.url('/tenants/current/dictionary-order'), dictionaryOrder);
  }

  connectRemote(url: string, username: string, password: string): Observable<RemoteTenantInfo[]> {
    return this.http.post<RemoteTenantInfo[]>(this.url('/tenants/import/connect'), {url, username, password});
  }

  /**
   * Ask the source server to create a pending export approval for the given tenant.
   * Returns a token that the source admin must approve before {@link importTenant} can succeed.
   */
  requestApproval(url: string, username: string, password: string,
                  remoteTenantId: number, selfInstanceUrl: string): Observable<ExportApprovalRequestResponse> {
    return this.http.post<ExportApprovalRequestResponse>(this.url('/tenants/import/request-approval'),
      {url, username, password, remoteTenantId, selfInstanceUrl});
  }

  /**
   * Attempt to import — also used as the polling probe.
   * Returns the full {@link HttpResponse} so callers can distinguish the polling
   * codes: 200 + body once approved (and the source consumes the token);
   * 202 (no body) while still PENDING; 404 once the token is gone.
   * {@code adminPassword} optionally overrides the auto-generated local admin password.
   */
  importTenant(url: string, username: string, password: string,
               remoteTenantId: number, exportToken: string,
               adminPassword?: string): Observable<HttpResponse<TenantConfiguration>> {
    return this.http.post<TenantConfiguration>(this.url('/tenants/import'),
      {url, username, password, remoteTenantId, exportToken, adminPassword: adminPassword || undefined},
      {context: suppressErrorLog(404), observe: 'response'});
  }

  // -- Source-side approval list (used by ExportApprovalsComponent) --

  listExportApprovals(): Observable<ExportApproval[]> {
    return this.http.get<ExportApproval[]>(this.url('/tenants/export-approvals'));
  }

  approveExport(token: string): Observable<ExportApproval> {
    return this.http.post<ExportApproval>(this.url(`/tenants/export-approvals/${encodeURIComponent(token)}/approve`), {});
  }

  denyExport(token: string): Observable<void> {
    return this.http.post<void>(this.url(`/tenants/export-approvals/${encodeURIComponent(token)}/deny`), {});
  }

  getTermWebConfig(tenantId: number): Observable<TermwebConfiguration[]> {
    return this.http.get<TermwebConfiguration[]>(this.url(`/tenants/${tenantId}/termweb`));
  }

  /**
   * Sets (sectionId != null) or clears (sectionId == null) the suggestion target section
   * for one dictionary on the current tenant.
   */
  setDictionarySuggestionSection(dictionaryId: number, sectionId: number | null): Observable<void> {
    return this.http.put<void>(this.url('/tenants/current/dictionary-suggestion-section'), {dictionaryId, sectionId});
  }

  saveTermWebConfig(tenantId: number, config: TermwebConfiguration): Observable<void> {
    return this.http.put<void>(this.url(`/tenants/${tenantId}/termweb`), config);
  }
}
